import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { Button } from 'components';
import { useAccountParam } from 'hooks';
import { useAccountId, useSetAccountId, useUserInfo } from 'state';
import { AccountInfo } from 'types';
import AccountSelection from '../AccountSelection/AccountSelection';
import styles from './SelectAccountButton.module.scss';

export default function SelectAccountButton() {
    const accountId = useAccountId();
    const [visible, setVisible] = useState(false);
    const toggleDialog = () => setVisible(!visible);
    const label = `${accountId ? 'CHANGE' : 'SELECT'} ACCOUNT`;

    return (
        <>
            <Button
                className={styles.button}
                fillMode="outline"
                themeColor="tertiary"
                onClick={toggleDialog}
            >
                {label}
            </Button>
            {visible && <AccountDialog label={label} close={toggleDialog} />}
        </>
    );
};

type AccountDialogProps = {
    label: string,
    close: () => void
};

const AccountDialog = ({ label, close } : AccountDialogProps) => {
    const [selectedAccount, setSelectedAccount] = useState<AccountInfo>();
    const setAccountId = useSetAccountId();
    const { isInternalUser } = useUserInfo();
    const accountId = useAccountParam();
    const location = useLocation();
    const navigate = useNavigate();
 
    const redirect = (toAccountId: string) => {
        if (toAccountId !== accountId) {
            if (location.pathname.startsWith('/accounts')) {
                const regex = new RegExp(/\/accounts\/[^/]+(\/[^/]+)(.*)?$/);
                let url = location.pathname.replace(regex, toAccountId ? `/accounts/${toAccountId}$1` : '$1');
                navigate(url);
            } else if (toAccountId) {
                const path = location.pathname.match(/^\/(deductions|disputes|invoices|payments|search)/)?.[1];
                if (path) {
                    navigate(`/accounts/${toAccountId}/${path}`);
                }
            }
        }
    }
    
    const setAccount = async () => {
        if (selectedAccount?.accountId) {
            setAccountId(selectedAccount.accountId);
            close();
            redirect(selectedAccount.accountId);
        }
    };

    const onSelect = (account: AccountInfo) => {
        setSelectedAccount(account);
    };

    const viewAll = () => {
        setAccountId('');
        close();
        redirect('');
    };

    return (
        <Dialog 
            title={`${accountId ? 'Change' : 'Select an'} Account`}
            width="30rem"
            onClose={close}
        >
            <div className={styles.selectionLabel}>
                Enter an account name or account number or select an account from the dropdown list
            </div>
            <div className={styles.selectionDropdown}>
                <AccountSelection onSelect={onSelect} selectedAccountId={accountId} />
            </div>
            <DialogActionsBar>
                <div className={styles.actionButtons}>
                    {isInternalUser && 
                        <Button
                            fillMode="outline"
                            themeColor="primary"
                            onClick={viewAll}
                        >
                            VIEW ALL
                        </Button>
                    }
                    <Button
                        fillMode="solid"
                        themeColor="primary"
                        onClick={setAccount}
                        disabled={!selectedAccount?.accountId || selectedAccount.accountId === accountId}
                    >
                        {label}
                    </Button>
                    <Button
                        fillMode="flat"
                        themeColor="primary"
                        onClick={close}
                    >
                        CANCEL
                    </Button>
                </div>
            </DialogActionsBar>
        </Dialog>
    );
};